import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";

export default function AddEmailPrompt() {
  const { needsEmailPrompt, dismissEmailPrompt, checkAuth, user } = useAuth();
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);

  if (!needsEmailPrompt) return null;

  const save = async (e) => {
    e.preventDefault();
    const emailVal = email.trim().toLowerCase();
    const waVal = whatsapp.trim();
    if (!emailVal && !waVal) {
      toast.error("Please enter your Email or WhatsApp number");
      return;
    }
    if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      toast.error("Please enter a valid email");
      return;
    }
    if (waVal && waVal.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid WhatsApp number");
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      if (emailVal) payload.email = emailVal;
      if (waVal) payload.phone = waVal;
      await api.put("/users/me", payload);
      await checkAuth();
      toast.success("Saved! Order updates will land here.");
      dismissEmailPrompt();
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save details");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="add-email-prompt" onClick={dismissEmailPrompt}>
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={dismissEmailPrompt} data-testid="add-email-skip"
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500">
          <X className="w-4 h-4"/>
        </button>
        <div className="w-12 h-12 rounded-2xl brand-gradient-pink flex items-center justify-center text-white shadow-md mb-3">
          <Mail className="w-6 h-6"/>
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-semibold">Add your email / WhatsApp number</h2>
        <p className="text-sm text-slate-600 mt-1">
          We'll send your order confirmations, shipping updates, and the occasional restock alert. Enter your Email or WhatsApp number — at least one is required, both are welcome.
        </p>
        <form onSubmit={save} className="mt-5 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-600">Email address</Label>
            <Input
              data-testid="add-email-input"
              type="email"
              placeholder="you@example.com"
              autoFocus
              className="mt-1.5 rounded-xl h-11"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-600">WhatsApp number</Label>
            <Input
              data-testid="add-whatsapp-input"
              type="tel"
              placeholder="10-digit or +91..."
              className="mt-1.5 rounded-xl h-11"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
            />
          </div>
          <p className="text-[11px] text-slate-400">Enter your Email or WhatsApp number — at least one is required.</p>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={dismissEmailPrompt} className="rounded-full flex-1" data-testid="add-email-later">
              Maybe later
            </Button>
            <Button type="submit" disabled={busy} data-testid="add-email-save"
              className="rounded-full flex-1 brand-gradient-pink text-white border-0">
              {busy ? "Saving…" : "Save details"}
            </Button>
          </div>
        </form>
        <p className="text-[11px] text-slate-400 mt-3 text-center">You can add or change this anytime from My Account.</p>
      </div>
    </div>
  );
}
